/**
 * Timezone-aware time formatting utilities.
 *
 * All display times in Station Explorer route through these helpers so a single
 * timezone preference (stored in mapPreferencesStore) applies everywhere.
 * Default is 'UTC' — the meteorological standard.
 *
 * Usage:
 *   const { timezone } = useMapPreferencesStore();
 *   formatTime(isoString, timezone)          → "07:30 AM"
 *   formatDateTime(isoString, timezone)      → "Aug 6, 07:30 AM"
 *   formatDate(isoString, timezone)          → "Aug 6"
 *   tzLabel(timezone)                        → "UTC" | "UTC+10" | "UTC-5"
 */

/** Format an ISO string or Date as HH:MM AM/PM in the given timezone. */
export function formatTime(iso: string | Date, timezone: string): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  try {
    return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', timeZone: timezone }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(d);
  }
}

/** Format an ISO string or Date as "Mon D, HH:MM AM/PM" in the given timezone. */
export function formatDateTime(iso: string | Date, timezone: string): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: timezone }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(d);
  }
}

/** Format an ISO string or Date as "Mon D" in the given timezone. */
export function formatDate(iso: string | Date, timezone: string): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: timezone }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
  }
}

/** Human-readable UTC offset label for a timezone at the current instant.
 *  e.g. "UTC", "UTC+10", "UTC-5:30" */
export function tzLabel(timezone: string): string {
  if (timezone === 'UTC') return 'UTC';
  try {
    // Extract the numeric offset from a short format string like "GMT+10:00"
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const offset = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
    // shortOffset is "GMT+10:00" → convert to "UTC+10"
    return offset.replace('GMT', 'UTC').replace(':00', '');
  } catch {
    return timezone;
  }
}

/** All IANA timezone strings available in this runtime, sorted.
 *  Used to populate the timezone selector. */
export const ALL_TIMEZONES: readonly string[] =
  // ponytail: Intl.supportedValuesOf is available in all modern browsers and Node 18+.
  // Ceiling: add a static fallback list if Safari <15.4 support is needed.
  (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl)
    ? (Intl as any).supportedValuesOf('timeZone') as string[]
    : ['UTC'];
